import { useState, useEffect, useCallback } from 'react';
import Sidebar from '../components/Sidebar';

// --- Type Definitions ---

interface Staff {
  userIdx: number;
  id: string;
  name: string;
  department: string;
  position: string;
  email: string;
  phone: string;
  status: string;
  joinedAt: string;
}

export interface Consultation {
  id: number;
  date: string;
  time: string;
  customerName: string;
  manager: string;
  type: string;
  status: string;
  memo: string;
  customerIdx: number;
  roomIdx?: number;
  assignedStaffIdx: number;
  roomName?: string;
  endTime?: string;
}

interface ScheduleRequest {
  category: string;
  type: string;
  customerName: string;
  title: string;
  startDt: string;
  endDt: string;
  customerIdx: number;
  roomIdx?: number;
  assignedStaffIdx: number;
  memo: string;
}

// API 응답 아이템 (유연한 타입)
interface ScheduleResponseItem {
  [key: string]: any; 
  scheduleIdx: number;
  category: string;
  type: string; 
  title: string;
  startDt: string;
  endDt: string;
  customerName: string;
  customerIdx: number;
  managerName: string;
  assignedStaffIdx: number;
  roomName?: string;
  roomIdx?: number;
  memo: string;
  status: string;
}

interface ApiResponse<T> {
  status: boolean;
  code: string;
  message: string;
  data: T;
}

interface Room {
  id: number;
  name: string;
  type: string;    
  capacity: number; 
  status: 'available' | 'occupied' | 'maintenance'; 
}

interface RoomResponseItem {
  roomIdx: number;
  name: string;
  type: string;
  isActive: string;
}

interface RoomReservation {
  id: string;
  roomId: number; 
  roomName: string;
  customerName: string;
  consultant: string;
  date: string;
  startTime: string;
  endTime: string;
  purpose: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
}

// ---------------------------

export default function ConsultationSchedule() {
  // --- State ---
  const [consultations, setConsultations] = useState<Consultation[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomReservations, setRoomReservations] = useState<RoomReservation[]>([]);

  const [viewMode, setViewMode] = useState<'calendar' | 'list' | 'rooms'>('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [roomTableDate, setRoomTableDate] = useState(new Date().toISOString().split('T')[0]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedConsultation, setSelectedConsultation] = useState<Consultation | null>(null);

  const [staffList, setStaffList] = useState<Staff[]>([]);
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0')); // 00 ~ 23시
  const minutesStep = ['00', '10', '20', '30', '40', '50']; // 10분 단위
  // 입력 폼 State
  const [newConsultation, setNewConsultation] = useState({
    date: '',
    time: '',
    endTime: '',
    customerName: '',
    customerIdx: 1,
    consultant: '', 
    consultantId: 0, 
    type: '방문',
    status: '예정',
    notes: '',
    room: '',
    roomId: 0
  });

  // --- Helpers ---
  const mapTypeToApi = (uiType: string) => {
    switch (uiType) {
      case '방문': return 'VISIT_CONSULT';
      case '전화': return 'CALL_CONSULT';
      default: return 'VISIT_CONSULT';
    }
  };

  const mapTypeToUi = (apiType: string) => {
    switch (apiType) {
      case 'VISIT_CONSULT': return '방문';
      case 'CALL_CONSULT': return '전화';
      default: return '기타';
    }
  };

  const mapStatusToUi = (apiStatus: string) => '예정';

  // ✅ [수정됨] 시간 계산 헬퍼: Date 객체 대신 문자열/숫자 연산 사용 (타임존 문제 해결)
  const calculateEndTimeStr = (dateStr: string, startTimeStr: string) => {
    const [hours, minutes] = startTimeStr.split(':').map(Number);
    let nextHour = hours + 1;
    // 24시 넘어가면 0으로 처리 (날짜 변경 로직은 제외하고 시간만 순환)
    if (nextHour >= 24) nextHour = 0;
    
    const h = String(nextHour).padStart(2, '0');
    const m = String(minutes).padStart(2, '0');
    return `${dateStr}T${h}:${m}:00`;
  };

  // ✅ [수정됨] 1시간 더하기 헬퍼: 문자열 기반
  const addOneHourStr = (timeStr: string) => {
    const [h, m] = timeStr.split(':').map(Number);
    let nextHour = h + 1;
    if (nextHour >= 24) nextHour = 0;
    return `${String(nextHour).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  // --- API Functions ---

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await fetch('https://api.wedmin.biz/user?status=ACTIVE&size=100', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const result = await response.json();
        if (result?.data?.items) {
          setStaffList(result.data.items);
        }
      } catch (error) {
        console.error('직원 목록 조회 실패:', error);
      }
    };
    fetchStaff();
  }, []);

  const fetchRooms = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/room?isActive=y&size=100', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const result = await response.json();
      const roomData = result.data?.items || result.data || [];

      if (Array.isArray(roomData)) {
        const mappedRooms: Room[] = roomData.map((item: RoomResponseItem) => ({
          id: Number(item.roomIdx), 
          name: item.name,
          type: item.type,
          capacity: 4, 
          status: 'available' 
        }));
        setRooms(mappedRooms);
      }
    } catch (error) {
      console.error('상담실 목록 조회 실패:', error);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);


  // 3. 상담 일정 조회 (GET)
  const fetchSchedules = useCallback(async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      
      const fromDt = firstDay.toISOString().split('T')[0];
      const toDt = lastDay.toISOString().split('T')[0];

      const token = localStorage.getItem('accessToken');

      const response = await fetch(`https://api.wedmin.biz/schedule/calendar?fromDt=${fromDt}&toDt=${toDt}&category=CONSULTATION`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('일정을 불러오는데 실패했습니다.');

      const json: ApiResponse<ScheduleResponseItem[]> = await response.json();
      
      if (json.status && json.data) {
        const mappedData: Consultation[] = json.data.map(item => {
          // ✅ [수정됨] 서버 응답 문자열을 안전하게 파싱 (Z 제거)
          const rawStart = item.startDt ? item.startDt.replace('Z', '') : '';
          const rawEnd = item.endDt ? item.endDt.replace('Z', '') : '';

          const dateStr = rawStart.includes('T') ? rawStart.split('T')[0] : rawStart;
          const timeStr = rawStart.includes('T') ? rawStart.split('T')[1].substring(0, 5) : '00:00';
          const endTimeStr = rawEnd.includes('T') ? rawEnd.split('T')[1].substring(0, 5) : '';

          const staffIdx = item.assignedStaffIdx 
            || item['assigned_staff_idx'] 
            || item.managerIdx 
            || item['manager_idx'] 
            || item.userIdx 
            || 0;

          const roomIdx = item.roomIdx 
            || item['room_idx'] 
            || item.roomId 
            || item['room_id'] 
            || 0;

          const customerIdx = item.customerIdx 
            || item['customer_idx'] 
            || item.customerId 
            || item['customer_id'] 
            || 0;

          return {
            id: item.scheduleIdx || item['schedule_idx'],
            date: dateStr,
            time: timeStr,
            endTime: endTimeStr,
            customerName: item.customerName,
            manager: item.managerName, 
            type: mapTypeToUi(item.type),
            status: mapStatusToUi(item.status),
            memo: item.memo,
            customerIdx: Number(customerIdx),
            roomIdx: Number(roomIdx),
            roomName: item.roomName,
            assignedStaffIdx: Number(staffIdx)
          };
        });
        setConsultations(mappedData);
      }
    } catch (error) {
      console.error("Schedule fetch error:", error);
    }
  }, [currentDate]);

  useEffect(() => {
    const reservations: RoomReservation[] = consultations
      .filter(c => c.roomIdx && c.roomIdx > 0)
      .map(c => ({
        id: c.id.toString(),
        roomId: c.roomIdx || 0,
        roomName: c.roomName || '알 수 없음',
        customerName: c.customerName,
        consultant: c.manager,
        date: c.date,
        startTime: c.time,
        // ✅ [수정됨] 문자열 기반 1시간 더하기
        endTime: c.endTime || addOneHourStr(c.time), 
        purpose: `${c.type} 상담`,
        status: 'scheduled'
      }));
    setRoomReservations(reservations);
  }, [consultations]);


  const createSchedule = async (scheduleData: ScheduleRequest) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch('https://api.wedmin.biz/schedule', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(scheduleData)
      });

      if (!response.ok) throw new Error('일정 등록에 실패했습니다.');

      const result = await response.json();
      if (result.status) {
        alert("일정이 등록되었습니다.");
        fetchSchedules();
        setShowAddForm(false);
        resetForm();
      } else {
        alert(`등록 실패: ${result.message}`);
      }
    } catch (error) {
      console.error("Create schedule error:", error);
      alert("일정 등록 중 오류가 발생했습니다.");
    }
  };

  const updateSchedule = async (scheduleIdx: number, scheduleData: ScheduleRequest) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/schedule/${scheduleIdx}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(scheduleData)
      });

      if (!response.ok) throw new Error('일정 수정에 실패했습니다.');

      const result = await response.json();
      if (result.status) {
        alert("일정이 수정되었습니다.");
        fetchSchedules();
        setShowAddForm(false);
        resetForm();
      } else {
        alert(`수정 실패: ${result.message}`);
      }
    } catch (error) {
      console.error("Update schedule error:", error);
      alert("일정 수정 중 오류가 발생했습니다.");
    }
  };

  // ✅ [New] 일정 삭제 함수
  const handleDeleteSchedule = async () => {
    if (!selectedConsultation) return;
    if (!confirm('정말 이 일정을 삭제하시겠습니까? (복구 불가)')) return;

    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/schedule/${selectedConsultation.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('일정 삭제에 실패했습니다.');

      // 204 No Content는 json() 파싱 불가
      if (response.status === 204 || response.ok) {
        alert("일정이 삭제되었습니다.");
        fetchSchedules(); // 목록 갱신
        setShowAddForm(false); // 모달 닫기
        resetForm();
      }
    } catch (error) {
      console.error("Delete schedule error:", error);
      alert("일정 삭제 중 오류가 발생했습니다.");
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);


  const resetForm = () => {
    setNewConsultation({
      date: '',
      time: '',
      endTime: '',
      customerName: '',
      customerIdx: 1,
      consultant: '',
      consultantId: 0,
      type: '방문',
      status: '예정',
      notes: '',
      room: '',
      roomId: 0
    });
    setSelectedConsultation(null);
  };

  const timeSlots = Array.from({ length: 13 }, (_, i) => {
    const hour = i + 9;
    return `${hour.toString().padStart(2, '0')}:00`;
  });

  const getDaysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  const formatDate = (year: number, month: number, day: number) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const getConsultationsForDate = (dateString: string) => consultations.filter(consultation => consultation.date === dateString);

  const getReservationForSlot = (roomId: number, time: string) => {
    return roomReservations.find(res => {
      if (Number(res.roomId) !== Number(roomId)) return false;
      if (res.date !== roomTableDate) return false;
      const resStart = parseInt(res.startTime.split(':')[0]);
      const resEnd = parseInt(res.endTime.split(':')[0]);
      const slotTime = parseInt(time.split(':')[0]);
      return slotTime >= resStart && slotTime < resEnd;
    });
  };

  const goToPreviousMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const goToNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const timeOptions = Array.from({ length: 24 * 6 }, (_, i) => {
    const h = Math.floor(i / 6);
    const m = (i % 6) * 10;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  });
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (newConsultation.consultantId === 0) {
      alert("담당자를 선택해주세요.");
      return;
    }
    
    if (!newConsultation.date || !newConsultation.time) {
        alert("날짜와 시작 시간을 확인해주세요.");
        return;
    }

    // ✅ [수정됨] 타임존 개입 없이 순수 문자열 조합으로 생성
    const startDt = `${newConsultation.date}T${newConsultation.time}:00`;
    let endDt = "";

    if (newConsultation.endTime && newConsultation.endTime !== "") {
        endDt = `${newConsultation.date}T${newConsultation.endTime}:00`;
    } else {
        // 종료 시간이 없으면 시작 시간 기준 1시간 뒤로 계산 (문자열 연산)
        endDt = calculateEndTimeStr(newConsultation.date, newConsultation.time);
    }

    const requestData: ScheduleRequest = {
      category: "CONSULTATION",
      type: mapTypeToApi(newConsultation.type),
      title: `${newConsultation.customerName} ${newConsultation.type} 상담`,
      startDt: startDt,
      endDt: endDt,
      customerIdx: newConsultation.customerIdx, 
      roomIdx: newConsultation.roomId > 0 ? newConsultation.roomId : undefined,
      assignedStaffIdx: newConsultation.consultantId,
      customerName: newConsultation.customerName || '',
      memo: newConsultation.notes
    };

    if (selectedConsultation) {
      updateSchedule(selectedConsultation.id, requestData);
    } else {
      createSchedule(requestData);
    }
  };

  const handleEditConsultation = (consultation: Consultation) => {
    setSelectedConsultation(consultation);
    
    let staffId = Number(consultation.assignedStaffIdx) || 0;
    if (staffId === 0 && consultation.manager) {
      const foundStaff = staffList.find(s => s.name === consultation.manager);
      if (foundStaff) staffId = foundStaff.userIdx;
    }

    let roomId = Number(consultation.roomIdx) || 0;
    if (roomId === 0 && consultation.roomName) {
      const foundRoom = rooms.find(r => r.name === consultation.roomName);
      if (foundRoom) roomId = Number(foundRoom.id);
    }

    setNewConsultation({
      date: consultation.date,
      time: consultation.time,
      endTime: consultation.endTime || '', 
      customerName: consultation.customerName,
      customerIdx: consultation.customerIdx,
      consultant: consultation.manager,
      
      consultantId: staffId, 
      
      type: consultation.type,
      status: consultation.status,
      notes: consultation.memo,
      
      room: consultation.roomName || '',
      roomId: roomId 
    });
    
    setShowAddForm(true);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];
    const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

    const weekHeader = weekDays.map(day => (
      <div key={day} className="p-2 text-center font-medium text-gray-600 bg-gray-50">{day}</div>
    ));

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="p-2 h-24 border border-gray-200"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = formatDate(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayConsultations = getConsultationsForDate(dateString);
      const isToday = dateString === new Date().toISOString().split('T')[0];
      const isSelected = dateString === selectedDate;

      days.push(
        <div
          key={day}
          className={`p-2 h-24 border border-gray-200 cursor-pointer hover:bg-gray-50 ${isToday ? 'bg-blue-50' : ''} ${isSelected ? 'bg-blue-100' : ''}`}
          onClick={() => setSelectedDate(dateString)}
        >
          <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-gray-900'}`}>{day}</div>
          <div className="space-y-1">
            {dayConsultations.slice(0, 2).map((consultation) => (
              <div
                key={consultation.id}
                className={`text-xs p-1 rounded truncate cursor-pointer ${consultation.type === '방문' ? 'bg-blue-100 text-blue-800' : consultation.type === '전화' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`}
                onClick={(e) => { e.stopPropagation(); handleEditConsultation(consultation); }}
              >
                {consultation.time} {consultation.customerName}
              </div>
            ))}
            {dayConsultations.length > 2 && <div className="text-xs text-gray-500">+{dayConsultations.length - 2}개 더</div>}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-7">{weekHeader}{days}</div>
      </div>
    );
  };

  const filteredConsultations = consultations.filter(consultation => 
    viewMode === 'list' ? consultation.date === selectedDate : true
  );

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800">상담일정</h1>
            <div className="flex gap-3">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button onClick={() => setViewMode('calendar')} className={`px-3 py-1 rounded-md text-sm whitespace-nowrap cursor-pointer ${viewMode === 'calendar' ? 'bg-white shadow-sm' : 'text-gray-600'}`}>캘린더</button>
                {/* <button onClick={() => setViewMode('list')} className={`px-3 py-1 rounded-md text-sm whitespace-nowrap cursor-pointer ${viewMode === 'list' ? 'bg-white shadow-sm' : 'text-gray-600'}`}>목록</button>
                <button onClick={() => setViewMode('rooms')} className={`px-3 py-1 rounded-md text-sm whitespace-nowrap cursor-pointer ${viewMode === 'rooms' ? 'bg-white shadow-sm' : 'text-gray-600'}`}>상담실 예약</button> */}
              </div>
              <button onClick={() => { resetForm(); setNewConsultation(prev => ({ ...prev, date: selectedDate })); setShowAddForm(true); }} className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-add-line mr-2"></i> 상담 등록
              </button>
            </div>
          </div>

          {viewMode === 'calendar' ? (
             <>
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-4">
                    <button onClick={goToPreviousMonth} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"><i className="ri-arrow-left-line text-gray-600"></i></button>
                    <h2 className="text-xl font-semibold text-gray-800">{currentDate.getFullYear()}년 {currentDate.getMonth() + 1}월</h2>
                    <button onClick={goToNextMonth} className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer"><i className="ri-arrow-right-line text-gray-600"></i></button>
                  </div>
                </div>
                {renderCalendar()}
                {selectedDate && (
                  <div className="mt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">{selectedDate} 상담 일정</h3>
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                      {getConsultationsForDate(selectedDate).length > 0 ? (
                        <div className="divide-y divide-gray-200">
                          {getConsultationsForDate(selectedDate).map((consultation) => (
                            <div key={consultation.id} className="p-4 hover:bg-gray-50">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="font-medium text-gray-900">{consultation.time}</span>
                                    <span className="font-medium text-gray-900">{consultation.customerName}</span>
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${consultation.type === '방문' ? 'bg-blue-100 text-blue-800' : consultation.type === '전화' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`}>{consultation.type}</span>
                                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${consultation.status === '완료' ? 'bg-green-100 text-green-800' : consultation.status === '예정' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{consultation.status}</span>
                                  </div>
                                  <div className="text-sm text-gray-600">담당자: {consultation.manager}</div>
                                </div>
                                <button onClick={() => handleEditConsultation(consultation)} className="text-teal-600 hover:text-teal-800 w-6 h-6 flex items-center justify-center cursor-pointer"><i className="ri-edit-line"></i></button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (<div className="p-8 text-center text-gray-500">선택된 날짜에 상담 일정이 없습니다.</div>)}
                    </div>
                  </div>
                )}
             </>
          ) : viewMode === 'list' ? (
             <>
                <div className="bg-white p-4 rounded-lg shadow mb-6">
                  <div className="flex items-center gap-4">
                    <label className="text-sm font-medium text-gray-700">날짜 선택:</label>
                    <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="border border-gray-300 rounded-md px-3 py-2" />
                  </div>
                </div>
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">날짜</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">시간</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">고객명</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">담당자</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">관리</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredConsultations.map((consultation) => (
                          <tr key={consultation.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{consultation.date}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{consultation.time}</td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{consultation.customerName}</td>
                            <td className="px-4 py-3 text-sm text-gray-900">{consultation.manager}</td>
                            <td className="px-4 py-3"><button onClick={() => handleEditConsultation(consultation)} className="text-teal-600 hover:text-teal-800 w-6 h-6 flex items-center justify-center cursor-pointer"><i className="ri-edit-line"></i></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
             </>
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">날짜 선택</label>
                  <input type="date" value={roomTableDate} onChange={(e) => setRoomTableDate(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent" />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="border border-gray-200 bg-gray-50 px-4 py-3 text-left text-sm font-semibold text-gray-700 sticky left-0 z-10">시간</th>
                        {rooms.map(room => (
                          <th key={room.id} className="border border-gray-200 bg-gray-50 px-4 py-3 text-center text-sm font-semibold text-gray-700 min-w-[180px]">
                            {room.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {timeSlots.map(time => (
                        <tr key={time}>
                          <td className="border border-gray-200 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 sticky left-0 z-10">{time}</td>
                          {rooms.map(room => {
                            const reservation = getReservationForSlot(room.id, time);
                            return (
                              <td key={room.id} className="border border-gray-200 px-2 py-2">
                                {reservation && reservation.startTime === time ? (
                                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
                                    <p className="text-sm font-semibold text-teal-900">{reservation.customerName}</p>
                                    <p className="text-xs text-teal-700 mt-1">{reservation.consultant}</p>
                                    <p className="text-xs text-teal-600 mt-1">{reservation.startTime} - {reservation.endTime}</p>
                                    <p className="text-xs text-gray-600 mt-1">{reservation.purpose}</p>
                                  </div>
                                ) : reservation ? (<div className="bg-teal-50 h-full"></div>) : (<div className="h-full"></div>)}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            </div>
          )}

          {showAddForm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-xl font-bold">{selectedConsultation ? '상담 수정' : '상담 등록'}</h2>
                  <button onClick={() => { setShowAddForm(false); resetForm(); }} className="text-gray-500 hover:text-gray-700 w-6 h-6 flex items-center justify-center cursor-pointer"><i className="ri-close-line text-xl"></i></button>
                </div>

                <form onSubmit={handleSubmit}>
                  <div className="space-y-4">
                    {/* ... (기존 필드들은 동일) ... */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">날짜 <span className="text-red-500">*</span></label>
                      <input type="date" required value={newConsultation.date} onChange={(e) => setNewConsultation({ ...newConsultation, date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        {/* 시작 시간 입력 (시 : 분 분리) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">시작 시간 <span className="text-red-500">*</span></label>
                            <div className="flex gap-2 items-center">
                                {/* 시간(Hour) 선택 */}
                                <select 
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    value={newConsultation.time ? newConsultation.time.split(':')[0] : '09'}
                                    onChange={(e) => {
                                        const currentMin = newConsultation.time ? newConsultation.time.split(':')[1] : '00';
                                        setNewConsultation({ ...newConsultation, time: `${e.target.value}:${currentMin}` });
                                    }}
                                >
                                    {hours.map(h => <option key={h} value={h}>{h}시</option>)}
                                </select>
                                <span className="text-gray-500">:</span>
                                {/* 분(Minute) 선택 - 10분 단위 */}
                                <select 
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    value={newConsultation.time ? newConsultation.time.split(':')[1] : '00'}
                                    onChange={(e) => {
                                        const currentHour = newConsultation.time ? newConsultation.time.split(':')[0] : '09';
                                        setNewConsultation({ ...newConsultation, time: `${currentHour}:${e.target.value}` });
                                    }}
                                >
                                    {minutesStep.map(m => <option key={m} value={m}>{m}분</option>)}
                                </select>
                            </div>
                        </div>

                        {/* 종료 시간 입력 (시 : 분 분리) */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">종료 시간 <span className="text-gray-500 text-xs">(선택)</span></label>
                            <div className="flex gap-2 items-center">
                                {/* 종료 시간(Hour) */}
                                <select 
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    value={newConsultation.endTime ? newConsultation.endTime.split(':')[0] : ''}
                                    onChange={(e) => {
                                        const currentMin = newConsultation.endTime ? newConsultation.endTime.split(':')[1] : '00';
                                        // 종료 시간이 비어있었다면 기본 '00'분으로 설정
                                        setNewConsultation({ ...newConsultation, endTime: `${e.target.value}:${currentMin || '00'}` });
                                    }}
                                >
                                    <option value="">선택</option>
                                    {hours.map(h => <option key={h} value={h}>{h}시</option>)}
                                </select>
                                <span className="text-gray-500">:</span>
                                {/* 종료 분(Minute) */}
                                <select 
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                                    value={newConsultation.endTime ? newConsultation.endTime.split(':')[1] : ''}
                                    onChange={(e) => {
                                        const currentHour = newConsultation.endTime ? newConsultation.endTime.split(':')[0] : '';
                                        if (currentHour) {
                                            setNewConsultation({ ...newConsultation, endTime: `${currentHour}:${e.target.value}` });
                                        }
                                    }}
                                >
                                    <option value="">선택</option>
                                    {minutesStep.map(m => <option key={m} value={m}>{m}분</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">고객명 <span className="text-red-500">*</span></label>
                      <input type="text" required value={newConsultation.customerName} onChange={(e) => setNewConsultation({ ...newConsultation, customerName: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">담당자 <span className="text-red-500">*</span></label>
                      <select required value={newConsultation.consultantId} onChange={(e) => { const id = Number(e.target.value); const selectedStaff = staffList.find(s => s.userIdx === id); setNewConsultation({ ...newConsultation, consultantId: id, consultant: selectedStaff ? selectedStaff.name : '' }); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer">
                        <option value={0}>선택하세요</option>
                        {staffList.map((staff) => (
                          <option key={staff.userIdx} value={staff.userIdx}>
                            {staff.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상담실</label>
                      <select 
                        value={newConsultation.roomId} // ID 바인딩
                        onChange={(e) => { 
                          const id = Number(e.target.value); 
                          const r = rooms.find(room => Number(room.id) === id); 
                          setNewConsultation({ ...newConsultation, roomId: id, room: r?.name || '' }); 
                        }} 
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                      >
                        <option value={0}>선택하세요 (선택사항)</option>
                        {rooms.map(r => (
                          <option key={r.id} value={r.id}>{r.name}</option> 
                        ))}
                      </select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">상담유형 <span className="text-red-500">*</span></label>
                        <select required value={newConsultation.type} onChange={(e) => setNewConsultation({ ...newConsultation, type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer">
                          <option value="">선택하세요</option>
                          <option value="방문">방문</option>
                          <option value="전화">전화</option>
                          <option value="메일">메일</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">상태 <span className="text-red-500">*</span></label>
                        <select required value={newConsultation.status} onChange={(e) => setNewConsultation({ ...newConsultation, status: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer">
                          <option value="예정">예정</option>
                          <option value="완료">완료</option>
                          <option value="취소">취소</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
                      <textarea value={newConsultation.notes} onChange={(e) => setNewConsultation({ ...newConsultation, notes: e.target.value })} rows={3} maxLength={500} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none" placeholder="최대 500자" />
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-6">
                    <button type="button" onClick={() => { setShowAddForm(false); resetForm(); }} className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap cursor-pointer">취소</button>
                    
                    {/* ✅ [NEW] 삭제 버튼 추가 (수정 모드일 때만 노출) */}
                    {selectedConsultation && (
                      <button 
                        type="button" 
                        onClick={handleDeleteSchedule} 
                        className="px-4 py-2 bg-red-50 text-red-600 border border-red-200 rounded-md hover:bg-red-100 whitespace-nowrap cursor-pointer mr-auto"
                      >
                        삭제
                      </button>
                    )}

                    <button type="submit" className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 whitespace-nowrap cursor-pointer">{selectedConsultation ? '수정' : '등록'}</button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}